/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { FF_APP_CREDIT_RISK } from '@/shared/utils/feature-flag';
import type { ClientRow } from '../../context/ClientManagementContext';
import { ClientReferralsPanel } from '../ClientReferralsPanel';
import type { ClientFormSection } from './client-quality';
import { RegistrationQualityCard } from './RegistrationQualityCard';
import { ResponsibleCard } from './ResponsibleCard';
import { ClientRolesCard } from './ClientRolesCard';
import { FinancialScoreCard } from './FinancialScoreCard';
import { EnrichmentCard } from './EnrichmentCard';

export interface ClientDetailSidebarProps {
  client: ClientRow;
  agencyId: string;
  onEdit: (section?: ClientFormSection) => void;
  onViewAttendance: (dealId: string) => void;
}

/**
 * The client page's right rail, shown beside every tab rather than only
 * the overview: what the registration is still missing, who is handling
 * the client, their roles, and the two credit cards behind their flag.
 *
 * Preferences moved into the overview tab, where they sit beside the
 * financial fields they qualify; the interaction list moved out entirely,
 * because the Histórico tab is the same list at full width.
 *
 * @param props - The client, the agency, and the two callbacks.
 * @returns The right rail.
 */
export function ClientDetailSidebar({
  client,
  agencyId,
  onEdit,
  onViewAttendance,
}: ClientDetailSidebarProps) {
  return (
    <aside
      data-testid="client-detail-sidebar"
      className="flex min-w-0 flex-col gap-(--space-4)"
    >
      <RegistrationQualityCard client={client} onEdit={onEdit} />

      <ResponsibleCard
        agencyId={agencyId}
        clientId={client.client_id}
        primaryBroker={client.primary_broker}
        capturedBy={client.captured_by}
        externalCaptor={client.external_captor}
        externalBroker={client.external_broker}
        onViewAttendance={onViewAttendance}
      />

      <ClientRolesCard roles={client.client_roles ?? []} onEdit={() => onEdit('personal')} />

      {FF_APP_CREDIT_RISK && (
        <>
          <FinancialScoreCard agencyId={agencyId} clientId={client.client_id} />
          <EnrichmentCard
            agencyId={agencyId}
            clientId={client.client_id}
            clientDocument={client.cpf || client.cnpj}
          />
        </>
      )}

      <ClientReferralsPanel agencyId={agencyId} clientId={client.client_id} />
    </aside>
  );
}
